import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
// hooks
import useSettings from '../../careflow/ph-core/multi-tenant/setting/useSettings';
import useResponsive from '../../hooks/useResponsive';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// config
import { HEADER, NAVBAR } from '../../config';
//
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import NavbarHorizontal from './navbar/NavbarHorizontal';
import VersioningFooter from 'src/components/VersioningFooter';
import Disclaimer from 'src/components/disclaimer/Disclaimer';
import useTenantThemeApi from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantThemeApi';
import useDisclaimerVisibility from 'src/hooks/useDisclaimerVisibility';

// ----------------------------------------------------------------------

type MainStyleProps = {
  collapseClick: boolean;
  showDisclaimer: boolean;
};

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick' && prop !== 'showDisclaimer',
})<MainStyleProps>(({ collapseClick, showDisclaimer, theme }) => ({
  flexGrow: 1,
  paddingTop: HEADER.MOBILE_HEIGHT + (showDisclaimer ? HEADER.DISCLAIMER_HEIGHT : 0) + 24,
  paddingBottom: HEADER.MOBILE_HEIGHT + 24,
  [theme.breakpoints.up('lg')]: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT + (showDisclaimer ? HEADER.DISCLAIMER_HEIGHT : 0) + 24,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create(['margin-left', 'padding-top'], {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && {
      marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    }),
  },
  background: '#f4f6f8', // TODO: take from palette
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const { disclaimerText, environment, showDisclaimer, isToClose } = useTenantThemeApi();
  const { isDismissed, dismissDisclaimer } = useDisclaimerVisibility();
  const location = useLocation();

  const { collapseClick, isCollapse } = useCollapseDrawer();
  const { themeLayout } = useSettings();
  const isDesktop = useResponsive('up', 'lg');
  const [open, setOpen] = useState(false);
  const verticalLayout = themeLayout === 'vertical';

  const disclaimerData = {
    disclaimerText,
    environment,
    isToClose: isToClose || false,
    dismissDisclaimer,
  };

  const shouldShowDisclaimer = showDisclaimer && !isDismissed;

  if (verticalLayout) {
    return (
      <>
        <DashboardHeader onOpenSidebar={() => setOpen(true)} verticalLayout={verticalLayout} />
        {isDesktop ? <NavbarHorizontal /> : <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />}
        {shouldShowDisclaimer && <Disclaimer {...disclaimerData} />}
        <Box
          component="main"
          sx={{
            px: { lg: 2 },
            pt: {
              xs: `${HEADER.MOBILE_HEIGHT + (shouldShowDisclaimer ? HEADER.DISCLAIMER_HEIGHT : 0) + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + (shouldShowDisclaimer ? HEADER.DISCLAIMER_HEIGHT : 0) + 24}px`,
            },
            pb: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 24}px`,
            },
          }}
        >
          <Outlet />
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
      }}
    >
      <DashboardHeader isCollapse={isCollapse} onOpenSidebar={() => setOpen(true)} />
      <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
      {shouldShowDisclaimer && <Disclaimer {...disclaimerData} />}
      <MainStyle collapseClick={collapseClick} showDisclaimer={shouldShowDisclaimer}>
        <Outlet />
        <VersioningFooter />
      </MainStyle>
    </Box>
  );
}
